/**
 * Catalogue des permissions métier (source de vérité, versionné ici et seedé en base).
 * Référence : docs/security/SECURITY_ARCHITECTURE.md §3.2 + décisions D-017, D-020, D-022 (2026-09-04).
 * Convention : `domaine.action`. ★ = sensible (audit systématique). ⇑ = à haut privilège.
 */
export const PERMISSION_DOMAINS = [
    'tenant',
    'settings',
    'user',
    'resource',
    'reservation',
    'customer',
    'compliance',
    'document',
    'task',
    'proforma',
    'invoice',
    'payment',
    'cash',
    'expense',
    'housekeeping',
    'maintenance',
    'notification',
    'report',
    'audit',
    'catalog',
    'table',
    'order',
    'printing',
    'folio',
    'stock',
    'crm',
    'support',
    'platform',
];
const p = (code, domain, description, flags = {}) => ({ code, domain, description, ...flags });
export const PERMISSIONS = [
    // tenant & properties
    p('tenant.read', 'tenant', 'Consulter la fiche du tenant (raison sociale, RCCM, IFU)'),
    p('tenant.update', 'tenant', 'Modifier la fiche tenant (mentions légales des factures)', {
        sensitive: true,
    }),
    p('property.read', 'tenant', 'Consulter les établissements'),
    p('property.create', 'tenant', 'Créer un établissement', { sensitive: true }),
    p('property.update', 'tenant', 'Modifier un établissement'),
    p('outlet.read', 'tenant', 'Consulter les points de vente'),
    p('outlet.manage', 'tenant', 'Créer et modifier les points de vente', { sensitive: true }),
    // settings
    p('settings.read', 'settings', 'Consulter les paramètres'),
    p('settings.update', 'settings', 'Modifier les paramètres généraux (numérotation, devise, politiques)', { sensitive: true }),
    p('settings.update_security', 'settings', 'Modifier les paramètres de sécurité (timeouts, MFA obligatoire, seuils)', { sensitive: true, highPrivilege: true }),
    p('branding.update', 'settings', 'Modifier logo, couleurs, pied de page des documents'),
    p('module.read', 'settings', "Consulter les modules activés et l'abonnement"),
    p('module.manage', 'settings', 'Activer ou désactiver un module pour un établissement', {
        sensitive: true,
        highPrivilege: true,
    }),
    p('tax.read', 'settings', 'Consulter les règles fiscales'),
    p('tax.manage', 'settings', 'Créer/modifier les règles de taxes, retenues, exonérations', {
        sensitive: true,
        highPrivilege: true,
    }),
    // users & roles
    p('user.read', 'user', 'Lister les utilisateurs du tenant'),
    p('user.create', 'user', 'Inviter un utilisateur', { sensitive: true }),
    p('user.update', 'user', "Modifier le profil ou l'e-mail d'un utilisateur", { sensitive: true }),
    p('user.deactivate', 'user', 'Désactiver / réactiver un compte', { sensitive: true }),
    p('user.reset_password', 'user', 'Déclencher une réinitialisation pour un autre utilisateur', {
        sensitive: true,
    }),
    p('role.read', 'user', 'Consulter rôles et permissions'),
    p('role.manage', 'user', 'Créer/modifier/supprimer des rôles personnalisés', {
        sensitive: true,
        highPrivilege: true,
    }),
    p('role.assign', 'user', 'Attribuer/retirer des rôles (avec scope établissement)', {
        sensitive: true,
        highPrivilege: true,
    }),
    p('session.revoke', 'user', "Révoquer les sessions d'un autre utilisateur", { sensitive: true }),
    p('mfa.enforce', 'user', 'Rendre la MFA obligatoire pour un rôle', { sensitive: true }),
    // resources
    p('resource.read', 'resource', 'Consulter hébergements et salles'),
    p('resource.create', 'resource', 'Créer une ressource'),
    p('resource.update', 'resource', 'Modifier une ressource (photos, caractéristiques)'),
    p('resource.archive', 'resource', 'Archiver une ressource', { sensitive: true }),
    p('rateplan.read', 'resource', 'Consulter les tarifs'),
    p('rateplan.manage', 'resource', 'Créer/modifier les plans tarifaires', { sensitive: true }),
    p('availability.read', 'resource', 'Consulter le calendrier de disponibilité'),
    p('availability.block', 'resource', 'Créer un blocage / bloc de maintenance'),
    // reservations
    p('reservation.read', 'reservation', 'Consulter les réservations'),
    p('reservation.create', 'reservation', 'Créer une réservation (brouillon / en attente)'),
    p('reservation.update', 'reservation', 'Modifier dates, occupants, extras (avant check-out)'),
    p('reservation.hold', 'reservation', 'Poser une option (blocage temporaire)'),
    p('reservation.confirm', 'reservation', 'Confirmer une réservation (avance enregistrée requise)'),
    p('reservation.confirm.without_deposit', 'reservation', 'Confirmer sans avance, motif obligatoire (D-020)', { sensitive: true }),
    p('reservation.cancel', 'reservation', 'Annuler (motif obligatoire, frais éventuels)', {
        sensitive: true,
    }),
    p('reservation.checkin', 'reservation', 'Effectuer le check-in'),
    p('reservation.checkout', 'reservation', 'Effectuer le check-out'),
    p('reservation.checkout.with_balance', 'reservation', 'Effectuer un check-out avec solde impayé, motif obligatoire (R-010)', { sensitive: true }),
    p('reservation.mark_noshow', 'reservation', 'Déclarer un no-show', { sensitive: true }),
    p('reservation.override_price', 'reservation', 'Réviser librement le tarif et la remise (latitude manager, D-022)', { sensitive: true }),
    // customers
    p('customer.read', 'customer', 'Consulter les clients (personnes, entreprises)'),
    p('customer.create', 'customer', 'Créer un client'),
    p('customer.update', 'customer', 'Modifier un client'),
    p('customer.merge', 'customer', 'Fusionner des doublons', { sensitive: true }),
    p('customer.export', 'customer', 'Exporter des données clients', {
        sensitive: true,
        highPrivilege: true,
    }),
    p('customer.anonymize', 'customer', "Pseudonymiser un client (droit à l'effacement)", {
        sensitive: true,
        highPrivilege: true,
    }),
    // compliance
    p('compliance.read', 'compliance', "Consulter l'état de conformité d'un séjour"),
    p('compliance.validate', 'compliance', "Valider ou rejeter une pièce justificative d'un séjour (D-013)", { sensitive: true }),
    p('compliance.manage_requirements', 'compliance', 'Configurer les exigences documentaires par type de ressource', { sensitive: true }),
    // documents
    p('document.read', 'document', 'Consulter les métadonnées des documents'),
    p('document.upload', 'document', 'Téléverser un document'),
    p('document.view_identity', 'document', "Ouvrir une pièce d'identité (accès journalisé nominativement)", { sensitive: true }),
    p('document.download', 'document', 'Télécharger les documents non sensibles (PDF facture, reçu)'),
    p('document.delete', 'document', 'Supprimer un document avant validation (motif)', {
        sensitive: true,
    }),
    // tâches (centre de tâches : documents manquants, paiement attendu…)
    p('task.read', 'task', 'Consulter le centre de tâches'),
    p('task.update', 'task', 'Traiter, clôturer ou écarter une tâche'),
    // proformas
    p('proforma.read', 'proforma', 'Consulter les proformas'),
    p('proforma.create', 'proforma', 'Créer / modifier un brouillon de proforma'),
    p('proforma.issue', 'proforma', 'Émettre une proforma (numérotation PRO/AAAA/NNNNN)'),
    p('proforma.cancel', 'proforma', 'Annuler une proforma'),
    // invoices
    p('invoice.read', 'invoice', 'Consulter les factures'),
    p('invoice.create', 'invoice', 'Créer / modifier un brouillon de facture'),
    p('invoice.issue', 'invoice', 'Émettre une facture définitive (contrôle de conformité R-001)', {
        sensitive: true,
    }),
    p('invoice.issue.override_compliance', 'invoice', 'Émettre malgré des documents manquants — dérogation R-001 avec motif obligatoire (D-017, Owner/Manager)', { sensitive: true, highPrivilege: true }),
    p('invoice.apply_discount', 'invoice', 'Appliquer une remise'),
    p('invoice.download_pdf', 'invoice', 'Télécharger le PDF'),
    p('creditnote.create', 'invoice', "Créer un avoir (seule voie d'annulation d'une facture émise)", { sensitive: true, highPrivilege: true }),
    // payments
    p('payment.read', 'payment', 'Consulter les paiements'),
    p('payment.record', 'payment', 'Enregistrer un paiement (espèces, Orange Money, virement…)'),
    p('payment.allocate', 'payment', 'Affecter un paiement à une facture / réservation'),
    p('payment.void', 'payment', 'Annuler un paiement (écriture compensatoire, motif obligatoire)', {
        sensitive: true,
        highPrivilege: true,
    }),
    p('payment.refund', 'payment', 'Enregistrer un remboursement', {
        sensitive: true,
        highPrivilege: true,
    }),
    // cash
    p('cash.read', 'cash', 'Consulter les sessions de caisse'),
    p('cash.open_session', 'cash', 'Ouvrir une session de caisse'),
    p('cash.record_movement', 'cash', 'Saisir un mouvement de caisse hors paiement'),
    p('cash.close_session', 'cash', 'Clôturer une session (comptage, écart)', { sensitive: true }),
    p('cash.reconcile', 'cash', 'Valider le rapprochement / justifier un écart', { sensitive: true }),
    // expense (sprint 2.9, D-052)
    p('expense.read', 'expense', 'Consulter les dépenses'),
    p('expense.record', 'expense', 'Enregistrer une dépense (espèces, mobile money, chèque, banque)'),
    p('expense.cancel', 'expense', 'Annuler une dépense (motif obligatoire)', { sensitive: true }),
    p('expense.export', 'expense', 'Exporter les dépenses', { sensitive: true }),
    // housekeeping & maintenance
    p('housekeeping.read', 'housekeeping', 'Consulter le statut ménage'),
    p('housekeeping.update_status', 'housekeeping', 'Changer le statut ménage'),
    p('maintenance.read', 'maintenance', 'Consulter les incidents'),
    p('maintenance.create', 'maintenance', 'Déclarer un incident'),
    p('maintenance.resolve', 'maintenance', 'Clôturer un incident / lever un blocage'),
    // notifications
    p('notification.read', 'notification', "Consulter l'historique des envois"),
    p('notification.send', 'notification', 'Envoyer manuellement un message au client'),
    p('notification.manage_templates', 'notification', 'Modifier les modèles de messages', {
        sensitive: true,
    }),
    // reports
    p('report.operational', 'report', "Rapports d'occupation, arrivées/départs"),
    p('report.financial', 'report', 'Rapports CA, encaissements, impayés', { sensitive: true }),
    p('report.export', 'report', 'Exporter des rapports', { sensitive: true }),
    // audit
    p('audit.read', 'audit', "Consulter le journal d'audit du tenant", { sensitive: true }),
    p('audit.export', 'audit', "Exporter le journal d'audit", {
        sensitive: true,
        highPrivilege: true,
    }),
    // catalogue de vente (H2, ADR-016)
    p('catalog.read', 'catalog', 'Consulter la carte (catégories, produits, options)'),
    p('catalog.manage', 'catalog', 'Créer et modifier la carte, les prix et les disponibilités', {
        sensitive: true,
    }),
    // salles et tables (H2, module Restaurant)
    p('table.read', 'table', 'Consulter les salles, les tables et le plan de salle'),
    p('table.manage', 'table', 'Créer et modifier les salles et les tables'),
    // commandes (H2, POS)
    p('order.read', 'order', 'Consulter les commandes et leur historique'),
    p('order.create', 'order', 'Ouvrir une commande et y ajouter des lignes'),
    p('order.send', 'order', 'Envoyer une commande en préparation (tickets cuisine)'),
    p('order.prepare', 'order', 'Passer une ligne ou un ticket en « prêt » / « servi » (cuisine, service)'),
    p('order.discount', 'order', 'Accorder une remise sur une commande (motif obligatoire)', {
        sensitive: true,
    }),
    p('order.cancel', 'order', 'Annuler une ligne envoyée ou une commande (motif obligatoire)', {
        sensitive: true,
    }),
    p('order.pay', 'order', 'Encaisser une commande (espèces, mobile money, carte…)'),
    p('order.transfer', 'order', 'Imputer une commande à un séjour en cours (« mettre sur la chambre », ADR-018)'),
    // impression (H2, ADR-017)
    // stock (H5, ADR-020, D-H34)
    p('stock.read', 'stock', 'Consulter les lieux, articles, niveaux, mouvements, achats et inventaires'),
    p('stock.manage', 'stock', 'Créer et modifier les lieux, les articles et les recettes de déstockage'),
    p('stock.move', 'stock', 'Saisir un ajustement, une perte ou un transfert (motif obligatoire)', {
        sensitive: true,
    }),
    p('stock.purchase', 'stock', 'Saisir et réceptionner les achats'),
    p('stock.count', 'stock', 'Ouvrir, saisir et valider un inventaire physique', {
        sensitive: true,
    }),
    p('printing.read', 'printing', 'Consulter les destinations d’impression et les tickets'),
    p('printing.manage', 'printing', 'Configurer les imprimantes et points de préparation', {
        sensitive: true,
    }),
    p('printing.reprint', 'printing', 'Réimprimer un ticket cuisine ou de caisse'),
    // folio de séjour (H3, ADR-018)
    p('folio.read', 'folio', 'Consulter le folio d’un séjour (charges, provenance, bons signés)'),
    p('folio.post', 'folio', 'Poser une charge manuelle sur un séjour et rattacher un bon signé'),
    p('folio.void', 'folio', 'Annuler une charge du folio (motif obligatoire) — rouvre la commande imputée', {
        sensitive: true,
    }),
    // CRM (H10, ADR-025, R-CRM-07)
    p('crm.read', 'crm', "Consulter la vue 360 des clients, leurs dépenses, les segments, les listes d'action et les rapports CRM"),
    p('crm.manage', 'crm', 'Configurer le programme de fidélité, ajuster des points, gérer les segments, les modèles et les réglages CRM', { sensitive: true }),
    p('crm.loyalty.redeem', 'crm', "Utiliser les points de fidélité d'un client à l'encaissement"),
    // support (ADR-022) : accès support consenti par le Owner
    p('support.read', 'support', 'Consulter les demandes et accès support du tenant'),
    p('support.decide', 'support', 'Approuver, refuser ou révoquer un accès support (consentement du Owner)', {
        sensitive: true,
        highPrivilege: true,
    }),
    // platform
    p('platform.manage_tenants', 'platform', 'Créer/suspendre des tenants, gérer les abonnements', {
        sensitive: true,
        highPrivilege: true,
        platformOnly: true,
    }),
    p('platform.support_access', 'platform', 'Accéder en lecture à un tenant avec accord du Owner (journalisé)', { sensitive: true, highPrivilege: true, platformOnly: true }),
    p('platform.read_health', 'platform', 'Consulter la santé de la plateforme', {
        platformOnly: true,
    }),
    p('platform.manage_permissions', 'platform', 'Faire évoluer le catalogue de permissions', {
        sensitive: true,
        highPrivilege: true,
        platformOnly: true,
    }),
    p('platform.manage_billing', 'platform', 'Facturer les abonnements : émettre, encaisser, annuler, tarifer (ADR-023)', { sensitive: true, highPrivilege: true, platformOnly: true }),
];
export const PERMISSION_CODES = PERMISSIONS.map((x) => x.code);
export const isPermissionCode = (v) => PERMISSION_CODES.includes(v);
/** Rôles système (docs/security/SECURITY_ARCHITECTURE.md §3.3, D-011). */
export const SYSTEM_ROLE_CODES = [
    'owner',
    'manager',
    'front_desk',
    'accountant',
    'housekeeping',
    'waiter',
    'cashier',
    'kitchen',
    'read_only',
    'platform_admin',
    'platform_support',
];
const all = (prefixes) => PERMISSION_CODES.filter((c) => prefixes.some((pre) => c.startsWith(pre)));
const READ_ALL = PERMISSION_CODES.filter((c) => /\.(read|read_health)$/.test(c) && !c.startsWith('platform.'));
/** Matrice rôle × permissions par défaut (seed). Modifiable uniquement via rôles personnalisés. */
export const SYSTEM_ROLE_PERMISSIONS = {
    owner: PERMISSION_CODES.filter((c) => !c.startsWith('platform.')),
    manager: PERMISSION_CODES.filter((c) => !c.startsWith('platform.') &&
        ![
            'settings.update_security',
            'tax.manage',
            'module.manage',
            'customer.anonymize',
            'audit.export',
            'platform.manage_permissions',
            'support.decide',
        ].includes(c)),
    front_desk: [
        ...all(['tenant.read', 'property.read', 'outlet.read', 'settings.read', 'tax.read']),
        ...all(['resource.read', 'rateplan.read', 'availability.']),
        'reservation.read',
        'reservation.create',
        'reservation.update',
        'reservation.hold',
        'reservation.confirm',
        'reservation.checkin',
        'reservation.checkout',
        'reservation.cancel',
        'folio.read',
        'folio.post',
        ...all(['customer.read', 'customer.create', 'customer.update']),
        // CRM (H10, D-H69) : la réception lit la vue 360 et utilise les points.
        'crm.read',
        'crm.loyalty.redeem',
        ...all([
            'compliance.read',
            'compliance.validate',
            'document.read',
            'document.upload',
            'document.view_identity',
            'document.download',
        ]),
        'task.read',
        'task.update',
        ...all(['proforma.']),
        'invoice.read',
        'invoice.create',
        'invoice.apply_discount',
        'invoice.download_pdf',
        'payment.read',
        'payment.record',
        'payment.allocate',
        'cash.read',
        'cash.open_session',
        'cash.record_movement',
        'expense.read',
        'expense.record',
        ...all(['housekeeping.', 'maintenance.read', 'maintenance.create']),
        'notification.read',
        'notification.send',
        'report.operational',
    ],
    accountant: [
        ...all([
            'tenant.read',
            'property.read',
            'settings.read',
            'tax.read',
            'resource.read',
            'rateplan.read',
            'availability.read',
        ]),
        'reservation.read',
        'order.read',
        'folio.read',
        'customer.read',
        'customer.export',
        'crm.read',
        'expense.read',
        'expense.record',
        'expense.export',
        'stock.read',
        'stock.purchase',
        'compliance.read',
        'document.read',
        'document.download',
        'task.read',
        ...all(['proforma.', 'invoice.']).filter((c) => c !== 'invoice.issue.override_compliance'),
        'creditnote.create',
        ...all(['payment.', 'cash.']),
        'notification.read',
        // La comptabilité émet les factures : elle doit pouvoir les transmettre au
        // client (`POST /invoices/:id/send`, sprint 2.6).
        'notification.send',
        'report.operational',
        'report.financial',
        'report.export',
        'audit.read',
    ],
    housekeeping: [
        'property.read',
        'resource.read',
        'availability.read',
        'housekeeping.read',
        'housekeeping.update_status',
        'maintenance.read',
        'maintenance.create',
        'task.read',
        'task.update',
    ],
    // Serveur (H2, D-H12) : prend et envoie les commandes, lit la carte et le plan de salle.
    waiter: [
        'tenant.read',
        'property.read',
        'outlet.read',
        'settings.read',
        'catalog.read',
        'table.read',
        'order.read',
        'order.create',
        'order.send',
        'order.prepare',
        'order.transfer',
        'printing.read',
        'printing.reprint',
        'customer.read',
        'task.read',
    ],
    // Caissier (H2, D-H12) : tout le serveur, plus encaissement, remise, annulation et caisse.
    cashier: [
        'tenant.read',
        'property.read',
        'outlet.read',
        'settings.read',
        'tax.read',
        'catalog.read',
        'table.read',
        ...all(['order.']),
        ...all(['printing.read', 'printing.reprint']),
        'customer.read',
        'customer.create',
        // CRM (H10, D-H69) : le caissier utilise les points ; palier et solde voyagent avec la commande.
        'crm.loyalty.redeem',
        'payment.read',
        'payment.record',
        'payment.allocate',
        'cash.read',
        'cash.open_session',
        'cash.record_movement',
        'cash.close_session',
        'expense.read',
        'expense.record',
        'task.read',
        'report.operational',
    ],
    // Cuisine (H2, D-H12) : lit les tickets, passe les lignes en prêt / servi, réimprime.
    kitchen: [
        'property.read',
        'outlet.read',
        'catalog.read',
        'stock.read',
        'order.read',
        'order.prepare',
        'printing.read',
        'printing.reprint',
    ],
    read_only: READ_ALL.filter((c) => !['document.view_identity', 'audit.read'].includes(c)),
    /**
     * Opérateurs de la plateforme HOREVIA (ADR-021, tenant technique `platform`) :
     * console (tenants, abonnements, modules) et administration de leurs collègues.
     */
    platform_admin: [
        ...all(['platform.']),
        ...all(['user.', 'role.', 'session.', 'audit.', 'tenant.', 'tax.']),
        // Identité de l'émetteur des factures d'abonnement (ADR-023, D-H53) : fiche,
        // logo et paramètres de facturation du tenant `platform`.
        'settings.read',
        'settings.update',
        'branding.update',
    ],
    platform_support: [
        'platform.read_health',
        'platform.support_access',
        'user.read',
        'role.read',
        'audit.read',
    ],
};
/** Rôles système réservés au tenant plateforme (`roles.is_platform`, R-PLT-02). */
export const PLATFORM_ROLE_CODES = [
    'platform_admin',
    'platform_support',
];
export const isPlatformRoleCode = (code) => PLATFORM_ROLE_CODES.includes(code);
/**
 * Permissions d'une session en mode support (ADR-022, R-SUP-02, A-072) : les
 * seuls codes `*.read` non réservés à la plateforme — ni export, ni
 * téléchargement, ni pièce d'identité, ni aucune écriture.
 */
export const SUPPORT_ACCESS_PERMISSIONS = READ_ALL;
//# sourceMappingURL=permissions.js.map